import {
  Autocomplete,
  Chip,
  InputAdornment,
  Stack,
  TextField
} from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import { sortSelectableTargetParams } from 'components/attacks/create/targets/target-utils'
import Icon from 'components/Icon'
import {
  formatTagsAndLabels,
  getSelectedClients,
  getTargetingStrategy,
  HOST_TARGETING_SECTION_COUNT,
  HOST_TARGETING_SECTION_DEFAULT_NAMES,
  HOST_TARGETING_SECTION_DISPLAY_NAMES
} from 'components/services/create/target-selection/service-target-utils'
import { ServiceTagLabelSection } from 'components/services/create/target-selection/ServiceTagLabelSection'
import { TagTargetingAutocompleteGroupItem } from 'components/services/create/target-selection/TagTargetingAutocompleteGroupItem'
import { Client } from 'types/api/attacks/client'
import {
  AutocompleteSearchOption,
  ServiceFormValues,
  TargetParams
} from 'types/forms'

function isTargetUnhealthy(target: Client) {
  return target.validation_results?.client_status === 'UNHEALTHY' ?? false
}

function getTargetParams(option, targetParams, selectedOptions) {
  // The MUI Autocomplete component with multiselect enabled returns an array of
  // selected options to the onChange function
  if (Array.isArray(option)) {
    return option.reduce((nextTargetParams, newValue) => {
      const { key, value } = newValue.params

      nextTargetParams[key] = nextTargetParams[key] || new Set()
      nextTargetParams[key].add(value)

      return nextTargetParams
    }, {})
  }

  const { key, value } = option.params

  const nextTargetParams = Object.assign({}, targetParams)
  nextTargetParams[key] = nextTargetParams[key] || new Set()

  const isNewOptionSelected = selectedOptions.some(
    (selectedOption) => selectedOption.params.value === value
  )

  if (isNewOptionSelected) {
    nextTargetParams[key].delete(value)
  } else {
    nextTargetParams[key].add(value)
  }

  return nextTargetParams
}

function getSelectedOptions(
  optionsList: AutocompleteSearchOption[],
  targetParams: TargetParams
) {
  const targetParamSectionsAndValues = {
    paramKeys: Object.keys(targetParams),
    paramValues: Object.values(targetParams).flatMap((targetParamValue) =>
      Array.from(targetParamValue)
    )
  }

  return optionsList.filter(
    (option) =>
      targetParamSectionsAndValues.paramKeys.includes(option.params.key) &&
      targetParamSectionsAndValues.paramValues.includes(option.params.value)
  )
}

export const ServiceTagTargetingSelection: React.FC<{
  clients: Client[]
  type?: ProvenanceTargetType
  setDisableFutureSteps(boolean): void
}> = ({ clients, type, setDisableFutureSteps }) => {
  const { getValues, resetField, setValue } =
    useFormContext<ServiceFormValues>()

  const { targetAll } = getValues()
  const targetParams = useWatch({ name: 'targetParams' })

  const labelKeys = useMemo(() => {
    const { labelKeys } = formatTagsAndLabels(clients, type, targetParams)
    return labelKeys
  }, [clients, type, targetParams])

  const { exposeClientStatus: isClientStatusEnabled } = useFlags()

  const sortedLabels = useMemo<{
    displayed: string[]
    others: string[]
  }>(() => {
    if (labelKeys) {
      return sortSelectableTargetParams(
        labelKeys,
        HOST_TARGETING_SECTION_DEFAULT_NAMES,
        HOST_TARGETING_SECTION_COUNT
      )
    }
    return { displayed: [], others: [] }
  }, [labelKeys])

  const displayedLabels: AutocompleteSearchOption[][] =
    sortedLabels.displayed.map((key) => {
      const { values = new Set() } = labelKeys[key] || {}

      return [...values].map((value: string) => ({
        section: key,
        label: value,
        params: {
          key,
          value
        }
      }))
    })

  const otherLabels: AutocompleteSearchOption[] = sortedLabels.others.flatMap(
    (key) => {
      if (!labelKeys[key]) return []

      const labelValues = labelKeys[key].values ?? new Set()

      return [...labelValues].map((value) => ({
        section: 'other',
        label: `${key}:${value}`,
        params: {
          key,
          value
        }
      }))
    }
  )

  // Autocomplete must have access to a flat map of all the clickable options
  const optionsList: AutocompleteSearchOption[] = displayedLabels
    .concat(otherLabels)
    .reduce((options, sortedLabel) => options.concat(sortedLabel), [])

  const selectedOptions = useMemo(() => {
    return getSelectedOptions(optionsList, targetParams)
  }, [optionsList, targetParams])

  const handleSelectOption = useCallback(
    (_e, newValue) => {
      const { type: targetType } = getValues()

      const nextTargetParams = getTargetParams(
        newValue,
        targetParams,
        selectedOptions
      )

      const newTargetingStrategy = getTargetingStrategy(
        targetType,
        nextTargetParams
      )

      const newSelectedOptions = getSelectedOptions(
        optionsList,
        nextTargetParams
      )

      const selectedClients = getSelectedClients(
        clients,
        targetType,
        newSelectedOptions,
        nextTargetParams,
        targetAll
      )

      resetField('processId')
      resetField('processName')
      setValue('selectedClients', selectedClients)
      setValue('targetAll', false)
      setValue('targetParams', nextTargetParams)
      setValue('targetingStrategy', newTargetingStrategy)
      setDisableFutureSteps(true)
    },
    [
      clients,
      getValues,
      optionsList,
      resetField,
      selectedOptions,
      setDisableFutureSteps,
      setValue,
      targetAll,
      targetParams
    ]
  )

  /**
   * There is an issue with TypeScript that necessitates casting the "clients" array to "any[]".
   * Reference for issues: https://github.com/microsoft/TypeScript/issues/44373
   * TODO: Refactor the .filter() expression to properly use custom types
   */
  const unhealthyClients: Client[] = isClientStatusEnabled
    ? (clients as any[]).filter(isTargetUnhealthy)
    : []

  return (
    <Stack data-test-tag-targeting-selection gap={2}>
      <Autocomplete
        data-test-filter-tag-search
        multiple
        data-testid="tag-targeting-autocomplete"
        groupBy={(option) => option.section}
        isOptionEqualToValue={(option, value) => option.label === value.label}
        options={optionsList}
        popupIcon={null}
        renderGroup={(params) => (
          <TagTargetingAutocompleteGroupItem
            key={params.group}
            params={params}
            type={type}
          />
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment
                    position="start"
                    sx={{
                      color: 'text.secondary',
                      marginLeft: 1,
                      marginRight: 1
                    }}
                  >
                    <Icon css={{ height: 14 }} icon="magnifyingGlass" />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              )
            }}
            label="Targets"
            placeholder={
              selectedOptions.length < 1
                ? 'Narrow the number of potential targets by tag'
                : null
            }
          />
        )}
        renderTags={(tags, getTagProps) =>
          tags.map((option, index) => (
            <Chip
              key={`tag-${option.uid}`}
              {...getTagProps({ index })}
              color="primary"
              data-testid="autocomplete-tag"
              label={option.label}
              size="small"
              variant="filled"
            />
          ))
        }
        value={selectedOptions}
        onChange={handleSelectOption}
      />
      <section>
        {sortedLabels.displayed.map((section) => (
          <ServiceTagLabelSection
            key={section}
            handleSelectOption={handleSelectOption}
            label={
              section in HOST_TARGETING_SECTION_DISPLAY_NAMES
                ? HOST_TARGETING_SECTION_DISPLAY_NAMES[section]
                : section
            }
            options={optionsList.filter(
              (options) => options.section === section
            )}
            selectedOptions={selectedOptions.filter(
              (selectedOption) => selectedOption.section === section
            )}
            unhealthyClients={unhealthyClients}
          />
        ))}
        {otherLabels.length > 0 && (
          <ServiceTagLabelSection
            handleSelectOption={handleSelectOption}
            label={displayedLabels.length > 0 ? 'Other Tags' : 'All Tags'}
            options={optionsList.filter(
              (options) => options.section === 'other'
            )}
            selectedOptions={selectedOptions.filter(
              (selectedOption) => selectedOption.section === 'other'
            )}
            unhealthyClients={unhealthyClients}
          />
        )}
      </section>
    </Stack>
  )
}
